// Phase 26D.3 — Day Summary report (Bizom reference layout).
// PJP vs NON-PJP quantity split per SKU, KG-primary, with total amount.
// Reuses the TransactionReportPage skeleton but renders a PCS reconciliation
// subtotal row under the KG totals so the field team can reconcile against
// old PCS-based worksheets.
import { useMemo, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Info, PackageCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import ExportButtons from "@/components/reports/ExportButtons";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import PageHeader from "@/components/PageHeader";

const TESTID = "day-summary-report";

export default function DaySummaryReport() {
  const reportsBackTo = useReportsBackLink();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState({
    date_from: today, date_to: today,
    zone_id: "", sub_zone_id: "", user_id: "",
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page: 1, page_size: 500 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get("/reports/day-summary", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const rows = data?.items || [];

  // Overall totals for footer strip
  const totals = useMemo(() => {
    if (!rows.length) return null;
    let pjp_kg = 0, non_pjp_kg = 0, pjp_pcs = 0, non_pjp_pcs = 0, amount = 0;
    for (const r of rows) {
      pjp_kg += Number(r.pjp_qty_kg) || 0;
      non_pjp_kg += Number(r.non_pjp_qty_kg) || 0;
      pjp_pcs += Number(r.pjp_qty_pcs) || 0;
      non_pjp_pcs += Number(r.non_pjp_qty_pcs) || 0;
      amount += Number(r.total_amount) || 0;
    }
    return {
      pjp_kg: round3(pjp_kg), non_pjp_kg: round3(non_pjp_kg),
      total_kg: round3(pjp_kg + non_pjp_kg),
      pjp_pcs, non_pjp_pcs, total_pcs: pjp_pcs + non_pjp_pcs,
      amount: Math.round(amount * 100) / 100,
    };
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`${TESTID}-page`}>
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: "Day Summary" }]}
        title="Day Summary"
        subtitle="End-of-day SKU quantity and amount, split between planned-beat (PJP) calls and other calls."
        favorite={{ key: "reports.day-summary", type: "report", label: "Day Summary", route: "/reports/day-summary" }}
        testid={`${TESTID}-page-header`}
      />

      <div className="flex items-start justify-end flex-wrap gap-2">
        <ExportButtons endpoint="/reports/day-summary" params={filters} filename="day-summary" />
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid={`${TESTID}-refresh`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                              showZone showSubZone showDateRange showUser showDistributor />
        </CardContent>
      </Card>

      {data?.filter_summary && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> {data.filter_summary} · {rows.length} SKUs
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[560px]">
            <table className="w-full text-xs" data-testid={`${TESTID}-table`}>
              <thead className="bg-[#6D2C91] text-white sticky top-0">
                <tr>
                  {columns.map(c => (
                    <th key={c.key}
                        className={
                          "text-left px-3 py-2 font-semibold whitespace-nowrap " +
                          (c.format === "kg" || c.format === "currency" || c.format === "number"
                            ? "text-right" : "")
                        }>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={columns.length || 1} className="text-center p-6 text-neutral-500">
                    No orders match the current filters.
                  </td></tr>
                ) : rows.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-neutral-50">
                    {columns.map(c => (
                      <td key={c.key}
                          data-testid={`${TESTID}-cell-${row.sku_code}-${c.key}`}
                          className={
                            "px-3 py-2 whitespace-nowrap " +
                            (c.format === "kg" || c.format === "currency" || c.format === "number"
                              ? "text-right font-mono tabular-nums" : "") +
                            (c.key === "total_qty_kg" ? " font-bold text-[#6D2C91]" : "")
                          }>
                        {renderCell(row[c.key], c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot className="bg-[#F5EEF8] border-t-2 border-[#6D2C91]">
                  {/* KG totals — primary */}
                  <tr data-testid={`${TESTID}-totals-kg`}>
                    <td colSpan={3} className="px-3 py-2 font-bold text-neutral-900">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold">
                      {fmtKg(totals.pjp_kg)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold">
                      {fmtKg(totals.non_pjp_kg)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-[#6D2C91]">
                      {fmtKg(totals.total_kg)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pjp_pcs.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.non_pjp_pcs.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.total_pcs.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-[#E5197F]">
                      ₹{totals.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  {/* PCS subtotal — reconciliation strip */}
                  <tr data-testid={`${TESTID}-totals-pcs`}>
                    <td colSpan={columns.length} className="px-3 py-1.5 text-[11px] text-neutral-600 flex items-center gap-2 justify-end">
                      <PackageCheck className="h-3 w-3 text-[#6D2C91]" />
                      PCS reconciliation: PJP {totals.pjp_pcs.toLocaleString("en-IN")} pcs · NON-PJP {totals.non_pjp_pcs.toLocaleString("en-IN")} pcs · Total {totals.total_pcs.toLocaleString("en-IN")} pcs
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function renderCell(v, col) {
  if (v == null || v === "") return "—";
  if (col.format === "kg") return fmtKg(v);
  if (col.format === "currency") {
    const n = Number(v);
    if (!isFinite(n)) return String(v);
    return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (col.format === "number") {
    const n = Number(v);
    if (!isFinite(n)) return String(v);
    return n.toLocaleString("en-IN");
  }
  return String(v);
}
function fmtKg(v) {
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function round3(v) { return Math.round(Number(v) * 1000) / 1000; }
